// 명식 · 네 기둥: the four pillars of /api/calculate as editable text and boxes, in the
// Saju app's element colours. The pillars of an unknown birth hour are left unknown.
import { OHAENG_COLORS, SUBJECT_KEYS, componentError, requestSaju, sameSubject, validateCalculation, validateSubject } from './component-saju.mjs';

const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const exactKeys = (value, keys) => object(value) && Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key));
const invalid = message => componentError('INVALID_COMPONENT_DATA', `${message} 기존 결과는 유지됩니다.`);
const text = (value, max) => typeof value === 'string' && value.length >= 1 && value.length <= max;
const texts = (value, count, max) => Array.isArray(value) && value.length <= count && value.every(item => text(item, max));

export const NATAL_SOURCE = 'sajug:/api/calculate';
const SOURCE = NATAL_SOURCE;
const STEMS = '갑을병정무기경신임계', BRANCHES = '자축인묘진사오미신유술해';
const ELEMENTS = Object.freeze(['목', '화', '토', '금', '수']);
// Semantic keys are ASCII (component keys are [\w.:-]); the order is how the app shows the pillars.
const PILLARS = Object.freeze([['hour', '시주'], ['day', '일주'], ['month', '월주'], ['year', '년주']]);
const ELEMENT_KEYS = Object.freeze({ 목: 'wood', 화: 'fire', 토: 'earth', 금: 'metal', 수: 'water' });
const ELEMENT_HANJA = Object.freeze({ 목: '木', 화: '火', 토: '土', 금: '金', 수: '水' });
const UNKNOWN = Object.freeze({ bg: '#ffffff', text: '#52665d' });
const PILLAR_FIELDS = ['key', 'stem', 'branch', 'stemHanja', 'branchHanja', 'stemOhaeng', 'branchOhaeng', 'stemTenGod', 'branchTenGod', 'unseong', 'hidden', 'shenshal'];

export function validatePaljaProps(props) {
  if (!object(props) || Object.keys(props).some(key => ![...SUBJECT_KEYS, 'title', 'accent'].includes(key))) throw componentError('INVALID_COMPONENT', '명식 설정 필드를 확인해 주세요.');
  validateSubject(props);
  if (typeof props.title !== 'string' || !props.title.trim() || props.title.length > 100 || !/^#[0-9a-f]{6}$/i.test(props.accent)) throw componentError('INVALID_COMPONENT', '제목과 강조색을 확인해 주세요.');
  return props;
}

function pillar(key, value) {
  if (value === null && key === 'hour') return null;
  if (!object(value) || !object(value.ganji)) throw invalid('네 기둥 계산 결과를 모두 받지 못했습니다.');
  const { stem, branch, stemHanja, branchHanja, stemOhaeng, branchOhaeng } = value.ganji;
  if (!text(stem, 1) || !STEMS.includes(stem) || !text(branch, 1) || !BRANCHES.includes(branch) || !text(stemHanja, 1) || !text(branchHanja, 1)
    || !ELEMENTS.includes(stemOhaeng) || !ELEMENTS.includes(branchOhaeng)) throw invalid('기둥의 천간·지지 형식이 올바르지 않습니다.');
  if (!text(value.stemTenGod, 10) || !text(value.branchTenGod, 10) || !text(value.unseong, 10) || !texts(value.shenshal, 16, 20)
    || !Array.isArray(value.hidden) || value.hidden.length > 3 || value.hidden.some(item => !object(item) || !text(item.stem, 1) || !STEMS.includes(item.stem) || !text(item.tenGod, 10))) {
    throw invalid('기둥의 십성·지장간·운성·신살 형식이 올바르지 않습니다.');
  }
  return { key, stem, branch, stemHanja, branchHanja, stemOhaeng, branchOhaeng, stemTenGod: value.stemTenGod, branchTenGod: value.branchTenGod,
    unseong: value.unseong, hidden: value.hidden.map(item => ({ stem: item.stem, tenGod: item.tenGod })), shenshal: [...value.shenshal] };
}

function relations(value) {
  if (!exactKeys(value, ['stems', 'branches'])) throw invalid('합충 계산 결과 형식이 올바르지 않습니다.');
  const side = entries => {
    if (!exactKeys(entries, ['hour', 'month', 'year']) || !Object.values(entries).every(list => texts(list, 10, 20))) throw invalid('기둥별 합충 형식이 올바르지 않습니다.');
    return { hour: [...entries.hour], month: [...entries.month], year: [...entries.year] };
  };
  return { stems: side(value.stems), branches: side(value.branches) };
}

/** Keep only the calculation fields the pillars show. */
export function normalizePaljaResponse(payload) {
  if (!object(payload) || !object(payload.saju) || !object(payload.birthInfo)) throw invalid('명식 계산 결과를 받지 못했습니다.');
  const { saju, birthInfo } = payload;
  if (!text(birthInfo.solar, 40) || !text(birthInfo.lunar, 80) || typeof birthInfo.unknownTime !== 'boolean') throw invalid('생년월일 계산 정보가 올바르지 않습니다.');
  if (!text(saju.bodyStrength, 20) || !exactKeys(saju.ohaengDist, ELEMENTS) || ELEMENTS.some(element => !Number.isInteger(saju.ohaengDist[element]) || saju.ohaengDist[element] < 0 || saju.ohaengDist[element] > 8)) {
    throw invalid('신강·신약과 오행 분포 형식이 올바르지 않습니다.');
  }
  const pillars = PILLARS.map(([key]) => pillar(key, saju[key]));
  if (pillars[0] === null && !birthInfo.unknownTime) throw invalid('시주 계산 결과가 없습니다.');
  return { birth: { solar: birthInfo.solar, lunar: birthInfo.lunar, unknownTime: birthInfo.unknownTime }, bodyStrength: saju.bodyStrength,
    ohaengDist: Object.fromEntries(ELEMENTS.map(element => [element, saju.ohaengDist[element]])), pillars, relations: relations(payload.relations) };
}

export async function calculatePalja(props, options = {}) {
  validatePaljaProps(props);
  const data = normalizePaljaResponse(await requestSaju('palja', props, options));
  return { source: SOURCE, calculatedAt: new Date().toISOString(), data };
}

/** A stored natal calculation (normalizePaljaResponse's shape): every pillar field present and nothing else. */
export function validateNatalData(data) {
  if (!exactKeys(data, ['birth', 'bodyStrength', 'ohaengDist', 'pillars', 'relations']) || !exactKeys(data.birth, ['solar', 'lunar', 'unknownTime'])
    || !Array.isArray(data.pillars) || data.pillars.length !== 4 || data.pillars.some(item => item !== null && !exactKeys(item, PILLAR_FIELDS))
    || data.pillars.some(item => item !== null && (!Array.isArray(item.hidden) || item.hidden.some(hidden => !exactKeys(hidden, ['stem', 'tenGod']))))) {
    throw componentError('INVALID_COMPONENT', '컴포넌트 결과의 필드가 누락되었거나 알 수 없는 필드가 있습니다.');
  }
  const saju = { bodyStrength: data.bodyStrength, ohaengDist: data.ohaengDist };
  PILLARS.forEach(([key], index) => {
    const item = data.pillars[index];
    if (item !== null && item.key !== key) throw componentError('INVALID_COMPONENT', '기둥 순서가 올바르지 않습니다.');
    saju[key] = item === null ? null : { ganji: { stem: item.stem, branch: item.branch, stemHanja: item.stemHanja, branchHanja: item.branchHanja,
      stemOhaeng: item.stemOhaeng, branchOhaeng: item.branchOhaeng }, stemTenGod: item.stemTenGod, branchTenGod: item.branchTenGod,
      unseong: item.unseong, hidden: item.hidden, shenshal: item.shenshal };
  });
  normalizePaljaResponse({ birthInfo: data.birth, relations: data.relations, saju });
}

export function renderPaljaScene(props, snapshot) {
  validatePaljaProps(props);
  const { data } = snapshot, elements = [];
  const rect = (key, x, y, width, height, fill, extra = {}) => elements.push({ key, type: 'rect', x, y, width, height, fill, ...extra });
  const write = (key, x, y, width, height, value, fontSize, color = '#24352f', fontFamily = 'Content KoPub Dotum') => elements.push({ key, type: 'text', x, y, width, height, text: value, fontSize, fontFamily, color });
  rect('background', 0, 0, 1000, 720, '#f6f8f6');
  write('title', 32, 22, 720, 44, props.title, 30);
  write('body-strength', 780, 26, 188, 36, data.bodyStrength, 24, props.accent);
  write('birth', 32, 72, 936, 24, `양력 ${data.birth.solar} · 음력 ${data.birth.lunar}${data.birth.unknownTime ? ' · 태어난 시각 모름' : ''}`, 15, '#52665d');
  PILLARS.forEach(([key, label], slot) => {
    const x = 36 + slot * 236, item = data.pillars[slot];
    write(`label-${key}`, x, 110, 220, 26, label, 18, '#52665d');
    // An unknown hour keeps the same objects, so recalculating with or without a birth hour keeps their design.
    const stem = item === null ? UNKNOWN : OHAENG_COLORS[item.stemOhaeng], branch = item === null ? UNKNOWN : OHAENG_COLORS[item.branchOhaeng];
    write(`stem-god-${key}`, x, 142, 220, 24, item === null ? '—' : item.stemTenGod, 16);
    rect(`stem-box-${key}`, x, 172, 220, 120, stem.bg, { stroke: '#d8e0dc', strokeWidth: 1 });
    write(`stem-${key}`, x + 78, 180, 80, 76, item === null ? '?' : item.stemHanja, 64, stem.text, 'Content KoPub Batang');
    write(`stem-name-${key}`, x + 12, 262, 196, 22, item === null ? '태어난 시각 모름' : `${item.stem} · ${item.stemOhaeng}(${ELEMENT_HANJA[item.stemOhaeng]})`, 15, stem.text);
    rect(`branch-box-${key}`, x, 300, 220, 120, branch.bg, { stroke: '#d8e0dc', strokeWidth: 1 });
    write(`branch-${key}`, x + 78, 308, 80, 76, item === null ? '?' : item.branchHanja, 64, branch.text, 'Content KoPub Batang');
    write(`branch-name-${key}`, x + 12, 390, 196, 22, item === null ? '시주를 계산하지 않음' : `${item.branch} · ${item.branchOhaeng}(${ELEMENT_HANJA[item.branchOhaeng]})`, 15, branch.text);
    write(`branch-god-${key}`, x, 428, 220, 24, item === null ? '—' : item.branchTenGod, 16);
    write(`hidden-${key}`, x, 460, 220, 44, item === null ? '지장간 —' : `지장간 ${item.hidden.map(hidden => `${hidden.stem}(${hidden.tenGod})`).join(' · ')}`, 14, '#52665d');
    write(`unseong-${key}`, x, 510, 220, 22, item === null ? '운성 —' : `운성 ${item.unseong}`, 14, '#52665d');
    write(`shenshal-${key}`, x, 538, 220, 60, item === null ? '신살 —' : item.shenshal.length ? `신살 ${item.shenshal.join(' · ')}` : '신살 없음', 13, '#52665d');
  });
  write('ohaeng-title', 36, 616, 110, 24, '오행 분포', 15, '#52665d');
  ELEMENTS.forEach((element, slot) => {
    const x = 150 + slot * 166, count = data.ohaengDist[element], color = OHAENG_COLORS[element];
    rect(`ohaeng-track-${ELEMENT_KEYS[element]}`, x, 622, 120, 12, '#e9eeeb');
    rect(`ohaeng-bar-${ELEMENT_KEYS[element]}`, x, 622, Math.max(1, 120 * count / 8), 12, color.accent, { dataProps: ['width'] });
    write(`ohaeng-${ELEMENT_KEYS[element]}`, x, 642, 120, 22, `${element}(${ELEMENT_HANJA[element]}) ${count}`, 15, color.text);
  });
  write('calculated-at', 32, 686, 936, 20, `계산: ${snapshot.calculatedAt.slice(0, 10)} · 사주 앱 명식 계산 결과`, 12, '#52665d');
  return { width: 1000, height: 720, elements };
}

export const PALJA_COMPONENT = Object.freeze({
  type: 'saju.palja', version: 1, label: '명식 · 네 기둥',
  description: '생년월일로 계산한 네 기둥(천간·지지·십성·지장간·운성·신살)과 오행 분포를 현재 페이지에 추가합니다.',
  fields: Object.freeze([
    Object.freeze({ name: 'title', label: '제목', kind: 'text', default: '사주팔자 · 네 기둥' }),
    Object.freeze({ name: 'accent', label: '강조색', kind: 'color', default: '#28745b' }),
  ]),
  validateProps: validatePaljaProps,
  validateSnapshot: snapshot => validateCalculation(snapshot, SOURCE, validateNatalData),
  calculate: calculatePalja, render: renderPaljaScene,
  calculationChanged: (before, after) => !sameSubject(before, after),
});
